import { useQuery } from '@tanstack/react-query';
import { useCurrentClientManager, useFeatureFlags } from '@cko/dashboard-shared';

import { API_PATHS } from '../../environment/apiPaths';
import { FeatureFlagId } from '../../config/shared/enums/featureFlagIds';
import {
  NetworkTokensSettingsResponse,
  useGetNetworkTokensSettingsFetcher,
} from './useGetNetworkTokensSettingsFetcher';

type Props = {
  enabled: boolean;
};

/**
 * Hook to check if the Merchant is onboarded for Network Tokens.
 *
 * @returns boolean - whether the current client is onboarded for Network Tokens.
 */
export const useIsNetworkTokensOnboardedMerchant = ({ enabled }: Props) => {
  const { getFeatureFlag } = useFeatureFlags();
  const { client } = useCurrentClientManager();
  const networkTokensSettingsFetcher = useGetNetworkTokensSettingsFetcher();
  const isNetworkTokensFeatureEnabled = getFeatureFlag(FeatureFlagId.NETWORK_TOKENS);

  const networkTokensSettingsQueryKeys = [
    API_PATHS.networkTokensApiBasePath,
    { clientId: client?.id },
  ];

  const isQueryEnabled = isNetworkTokensFeatureEnabled && enabled;
  const {
    data,
    isLoading: isNetworkTokensLoading,
    isError,
  } = useQuery<NetworkTokensSettingsResponse>(
    networkTokensSettingsQueryKeys,
    networkTokensSettingsFetcher,
    {
      enabled: isQueryEnabled,
    },
  );

  const isLoading = isNetworkTokensLoading && isQueryEnabled;

  return {
    isNetworkTokensOnboardedMerchant: data?.onboarded,
    isLoading,
    isError,
  };
};
